/**
 * Workflow Folders API Service
 *
 * HTTP client for workflow folders module endpoints.
 * Follows established patterns from workflowsApi.
 */

import { api } from '@/lib/api';
import type {
  WorkflowFolderResponse,
  WorkflowFolderListResponse,
  CreateWorkflowFolderRequest,
  UpdateWorkflowFolderRequest,
  WorkflowFolderQuery,
  WorkflowUsageType,
} from '@troia-v3/types';

/**
 * API response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

/**
 * Workflow Folders API endpoints
 */
export const workflowFoldersApi = {
  // ============================================
  // Folder CRUD Operations
  // ============================================

  /**
   * Create a new folder
   */
  async create(data: CreateWorkflowFolderRequest): Promise<WorkflowFolderResponse> {
    const response = await api.post<ApiResponse<WorkflowFolderResponse>>('/workflow-folders', data);
    return response.data.data;
  },

  /**
   * Get folder by ID
   */
  async getById(id: string): Promise<WorkflowFolderResponse> {
    const response = await api.get<ApiResponse<WorkflowFolderResponse>>(`/workflow-folders/${id}`);
    return response.data.data;
  },

  /**
   * List folders with pagination and filters
   */
  async list(query?: WorkflowFolderQuery): Promise<WorkflowFolderListResponse> {
    const response = await api.get<ApiResponse<WorkflowFolderListResponse>>('/workflow-folders', {
      params: query,
    });
    return response.data.data;
  },

  /**
   * Get root folders (folders without parent).
   * `usageType` seleciona a árvore — pastas de Workflows (automation) e de
   * Skills são árvores separadas.
   */
  async getRootFolders(usageType?: WorkflowUsageType): Promise<WorkflowFolderResponse[]> {
    const response = await api.get<ApiResponse<WorkflowFolderResponse[]>>('/workflow-folders/root', {
      params: usageType ? { usageType } : undefined,
    });
    return response.data.data;
  },

  /**
   * Update folder
   */
  async update(id: string, data: UpdateWorkflowFolderRequest): Promise<WorkflowFolderResponse> {
    const response = await api.patch<ApiResponse<WorkflowFolderResponse>>(`/workflow-folders/${id}`, data);
    return response.data.data;
  },

  /**
   * Delete folder (soft delete)
   * @param force - If true, moves workflows to root instead of blocking deletion
   */
  async delete(id: string, force: boolean = false): Promise<void> {
    await api.delete(`/workflow-folders/${id}`, {
      params: { force },
    });
  },

  // ============================================
  // Utility Operations
  // ============================================

  /**
   * Get subfolders of a parent folder (opcionalmente restrito à árvore)
   */
  async getSubfolders(parentId: string, usageType?: WorkflowUsageType): Promise<WorkflowFolderResponse[]> {
    const response = await api.get<ApiResponse<WorkflowFolderListResponse>>('/workflow-folders', {
      params: { parentId, ...(usageType ? { usageType } : {}) },
    });
    return response.data.data.items;
  },

  /**
   * Get ancestors (breadcrumb path) for a folder
   * Returns array from root to parent (excludes current folder)
   */
  async getAncestors(folderId: string): Promise<WorkflowFolderResponse[]> {
    const ancestors: WorkflowFolderResponse[] = [];
    let currentFolder = await this.getById(folderId);

    // Walk up the tree collecting ancestors
    while (currentFolder.parentId) {
      const parent = await this.getById(currentFolder.parentId);
      ancestors.unshift(parent); // Add to beginning to maintain order from root
      currentFolder = parent;
    }

    return ancestors;
  },
};

export default workflowFoldersApi;
